import { Toggle as BaseToggle } from "@base-ui/react/toggle";
import { ToggleGroup as BaseToggleGroup } from "@base-ui/react/toggle-group";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "../lib/cn";

export const toggleVariants = cva(
  "inline-flex shrink-0 items-center justify-center gap-1.5 rounded-md text-sm font-medium whitespace-nowrap text-fg-muted transition-colors duration-fast outline-none select-none focus-ring hover:bg-fg/6 hover:text-fg data-pressed:bg-fg/8 data-pressed:text-fg data-disabled:pointer-events-none data-disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        default: "bg-transparent",
        outline: "border border-border bg-transparent data-pressed:border-border-strong",
      },
      size: {
        sm: "h-7 min-w-7 px-1.5 text-xs",
        md: "h-8 min-w-8 px-2",
        lg: "h-9 min-w-9 px-2.5",
      },
    },
    defaultVariants: { variant: "default", size: "md" },
  },
);

export interface ToggleProps
  extends Omit<BaseToggle.Props, "className">, VariantProps<typeof toggleVariants> {
  className?: string;
}

export function Toggle({ className, variant, size, ...props }: ToggleProps) {
  return (
    <BaseToggle
      data-slot="toggle"
      className={cn(toggleVariants({ variant, size }), className)}
      {...props}
    />
  );
}

export interface ToggleGroupProps extends Omit<BaseToggleGroup.Props, "className"> {
  className?: string;
}

/** A row of `Toggle`s sharing one `value` array. Set `multiple` for multi-select. */
export function ToggleGroup({ className, ...props }: ToggleGroupProps) {
  return (
    <BaseToggleGroup
      data-slot="toggle-group"
      className={cn(
        "inline-flex items-center gap-0.5 rounded-md data-[orientation=vertical]:flex-col",
        className,
      )}
      {...props}
    />
  );
}
